import { Row } from "@tanstack/react-table";
import { format, parseISO } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  AnalysisCell,
  ColumnOneTableCellSkeleton,
  MultipleReportsModal,
  PageNotFound,
  SessionDetailsHeaderSkeleton,
  SessionErrorModal,
  Table,
  TableCellSkeleton,
} from "../../../components";
import {
  columnHelper,
  SharedSessionColumns,
} from "../../../components/shared/SharedSessionColumns";
import { useModalContext } from "../../../contexts/ModalContext";
import { useUIContext } from "../../../contexts/UIContext";
import { useVisualizerContext } from "../../../contexts/VisualizerContext";
import { buildMovementTypeOptions } from "../../../features/DataVisualizer/utils/visualizerHelpers";
import { useAppSelector } from "../../../redux/hooks";
import {
  useGetReportsBySessionIdQuery,
  useGetSessionByIdQuery,
} from "../../../services";
import { normalizeFetchError } from "../../../shared/Constants";
import {
  parseDataForSessionDetails,
  processNumMovements,
  showPlayerHandedness,
} from "../../../shared/Functions";
import { MovementType } from "../../../types";

type SessionRouteParams = {
  sessionId: string;
};

export const SessionDetails: React.FC = (): JSX.Element => {
  const sessionRouteParams = useParams<SessionRouteParams>();
  const sessionId = sessionRouteParams.sessionId;

  // State and AppContext
  const { setDisplayBack } = useUIContext();
  const { setModalState } = useModalContext();
  const { setVisualizerInfo, setIsVisualizerModalOpen } =
    useVisualizerContext();
  const [selectedPageSize, setSelectedPageSize] = useState(
    sessionStorage.getItem("sessionPageSize") || "10",
  );
  const [errorModalSession, setErrorModalSession] = useState<any | null>(null);
  const [errorModalOrgPlayerId, setErrorModalOrgPlayerId] = useState<
    string | undefined
  >(undefined);
  const [errorModalFailedMovementsCount, setErrorModalFailedMovementsCount] =
    useState<number | undefined>(undefined);

  // Redux calls
  const orgType = useAppSelector((state) => state.org.type);
  const movementTypeSlugs = useAppSelector(
    (state) => state.org,
  ).details.movementTypes.map((type: MovementType) => type.slug);

  // RTK Queries
  const {
    data: selectedSession,
    isFetching: fetchingSessionDetails,
    isError: sessionError,
  } = useGetSessionByIdQuery(sessionId!);
  const { data: sessionReports, isFetching: fetchingReports } =
    useGetReportsBySessionIdQuery(
      {
        sessionId,
        movementTypes: movementTypeSlugs,
      },
      { skip: !selectedSession },
    );

  const playersFromSelectedSession = useMemo(
    () => (selectedSession ? selectedSession.players : []),
    [selectedSession],
  );

  const handleErrorClick = useCallback(
    (session: any, orgPlayerId?: string, failedMovementsCount?: number) => {
      setErrorModalSession(session);
      setErrorModalOrgPlayerId(orgPlayerId);
      setErrorModalFailedMovementsCount(failedMovementsCount);
    },
    [],
  );

  const sessionDetailsData = useMemo(
    () =>
      parseDataForSessionDetails(
        playersFromSelectedSession,
        selectedSession,
        sessionReports,
        handleErrorClick,
      ),
    [
      playersFromSelectedSession,
      selectedSession,
      sessionReports,
      handleErrorClick,
    ],
  );

  const memoizedSharedSessionColumns = useMemo(SharedSessionColumns, []);

  const sessionDetailColumns = useMemo(
    () => [
      columnHelper.accessor("playerName", {
        header: "Player",
        size: 300,
        cell: function playerNameCell(element: any) {
          const playerName = element.getValue();
          return (
            <Link
              to={`/player/${element.row.original.orgPlayerId}`}
              className="font-bold text-primary-700 hover:underline"
              data-testid="player-link"
            >
              {playerName || ""}
            </Link>
          );
        },
      }),
      memoizedSharedSessionColumns[3],
      memoizedSharedSessionColumns[4],
      memoizedSharedSessionColumns[5],
      columnHelper.accessor("hand", {
        header: "Hand",
        sortingFn: (rowA: Row<any>, rowB: Row<any>) => {
          const handsRowA = rowA.original.hand.split(", ");
          const handsRowB = rowB.original.hand.split(", ");
          if (handsRowA[0] > handsRowB[0]) {
            return 1;
          }
          if (handsRowB[0] > handsRowA[0]) {
            return -1;
          }
          return 0;
        },
        sortDescFirst: false,
        cell: function handCell(element: any) {
          return <p data-testid="hand-cell">{element.getValue()}</p>;
        },
      }),
      columnHelper.accessor("analysis", {
        header: "Actions",
        enableSorting: false,
        cell: function analysisCell(element: any) {
          const player = element.row.original;
          const movementTypes = Object.keys(player.totalMovements);

          const { movementTypeOptions, singleMovementHandler, canVisualize } =
            buildMovementTypeOptions(
              movementTypes,
              selectedSession,
              "session",
              setVisualizerInfo,
              setIsVisualizerModalOpen,
            );

          return (
            <AnalysisCell
              element={element}
              fetchingReports={fetchingReports}
              session={selectedSession}
              openReportsModal={viewSessionReports}
              showVisualizeButton={canVisualize}
              handleVisualizeButton={singleMovementHandler}
              visualizeButtonData={player}
              movementTypeOptions={
                movementTypeOptions.length > 1 ? movementTypeOptions : undefined
              }
            />
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchingReports, memoizedSharedSessionColumns, selectedSession],
  );

  const columnsWithoutHand = useMemo(
    () =>
      sessionDetailColumns.filter((column) => column.accessorKey !== "hand"),
    [sessionDetailColumns],
  );

  const tableData = useMemo(
    () => (fetchingSessionDetails ? Array(10).fill({}) : sessionDetailsData),
    [fetchingSessionDetails, sessionDetailsData],
  );

  const tableColumns = useMemo(
    () =>
      fetchingSessionDetails
        ? (!showPlayerHandedness(orgType)
            ? columnsWithoutHand
            : sessionDetailColumns
          ).map((column, index) => ({
            ...column,
            cell:
              index === 0 ? (
                <ColumnOneTableCellSkeleton />
              ) : (
                <TableCellSkeleton />
              ),
          }))
        : !showPlayerHandedness(orgType)
          ? columnsWithoutHand
          : sessionDetailColumns,
    [columnsWithoutHand, fetchingSessionDetails, orgType, sessionDetailColumns],
  );

  const viewSessionReports = (player: any, session: any) => {
    const numberOfMovements = processNumMovements(
      player.numberOfMovements,
      session.sessionType.slug,
      true,
    );
    setModalState((prev) => ({
      ...prev,
      shouldOpen: true,
      title: `${format(parseISO(session.sessionDate), "M/d/yyyy")} - ${
        session.sessionType.name
      } - ${numberOfMovements}`,
      singleStep: <MultipleReportsModal reports={player.reports} />,
    }));
  };

  useEffect(() => {
    setDisplayBack && setDisplayBack(true);
  }, [setDisplayBack]);

  return (
    <>
      <SessionErrorModal
        isOpen={errorModalSession !== null}
        onClose={() => {
          setErrorModalSession(null);
          setErrorModalOrgPlayerId(undefined);
          setErrorModalFailedMovementsCount(undefined);
        }}
        session={errorModalSession}
        orgPlayerId={errorModalOrgPlayerId}
        failedMovementsCount={errorModalFailedMovementsCount}
      />
      {!fetchingSessionDetails && !selectedSession ? (
        <PageNotFound />
      ) : (
        <main className="relative pb-20 px-4 z-0 overflow-y-auto w-full">
          {fetchingSessionDetails ? (
            <SessionDetailsHeaderSkeleton />
          ) : selectedSession ? (
            <div
              className="flex flex-col justify-start mt-8 max-w-7xl px-4"
              data-testid="session-details-header"
            >
              <p
                className="text-base px-4 text-gray-400 font-bold"
                data-testid="session-date"
              >
                {format(parseISO(selectedSession.sessionDate), "M/d/yyyy")}
              </p>
              <h2
                className="max-w-7xl mt-8 px-4 pl-4 text-3xl leading-6 font-bold text-gray-900"
                data-testid="session-type"
              >
                {selectedSession.sessionType.name}
              </h2>
              <p
                className="text-base px-4 mt-8 text-gray-400 font-bold"
                data-testid="session-venue"
              >
                {selectedSession.sessionVenue !== null
                  ? selectedSession.sessionVenue
                  : "Default Venue"}
              </p>
            </div>
          ) : null}
          <div className="mt-8" data-testid="session-details-table-container">
            <Table
              columns={tableColumns}
              data={tableData}
              data-testid="session-details-table"
              selectedPageSize={selectedPageSize}
              setSelectedPageSize={setSelectedPageSize}
              displayFooter={true}
              fetching={fetchingSessionDetails}
              fetchError={sessionError ? normalizeFetchError() : null}
              customRowClassName="session-details-row"
            />
          </div>
        </main>
      )}
    </>
  );
};
